// Единый сервис корзины: кэш /cart.js, сериализованные идемпотентные мутации
// с лимитами количества, нормализация ошибок cart API.
// UI-компоненты не ходят в /cart/*.js напрямую — только через window.CartService.
//
// Правила (аудит корзины 23.10.2026, docs/cart-audit-2026-10-23.md):
//   - одно действие покупателя = один запрос, где это позволяет Ajax API:
//     /cart/update.js принимает сразу updates (несколько строк по key или
//     variant id, 0 = удалить), attributes и sections — им заменены пары
//     clear.js + update.js (clear.js атрибуты не трогает);
//   - ответ change/update/clear — целиковая корзина, он кладётся в кэш
//     (primeCache), чтобы потребители не перечитывали /cart.js; ответ add.js —
//     только добавленные строки, после него кэш сбрасывается;
//   - бизнес-правила (что снять вместе со строкой) живут в assets/club-cart.js
//     (MFClub.removalPlan) и возвращают план, а не шлют запросы;
//   - корзину пишем не мы одни (QA-раунд MFS-5933, 28–29.10.2026): апп Service
//     Fee оборачивает fetch и после каждого нашего ответа асинхронно пишет свой
//     сбор, а Shopify при двух параллельных записях ТЕРЯЕТ одну из них (наш
//     add.js отвечал 200, а корзина оставалась пустой). Поэтому сборка корзины
//     под чекаут (prepareCheckout) не верит ни снимку, ни ответу add.js: после
//     записи она перечитывает корзину и чинит расхождения, пока они не
//     исчезнут (settleCheckout), а update.js по исчезнувшему ключу (422)
//     повторяется по свежей корзине (updateCore).
(function () {
  "use strict";

  if (window.CartService) return;

  const CART_CACHE_TTL = 8000;
  // Settle: сколько раз чиним корзину после add, сколько ждём перед контрольным
  // чтением после ремонта и общий бюджет (медленная сеть + ожидание ответа
  // аппа внутри каждого нашего fetch).
  const SETTLE_MAX_REPAIRS = 3;
  const SETTLE_WINDOW_MS = 500;
  const SETTLE_BUDGET_MS = 8000;

  let cartCache = null;
  let cartCacheAt = 0;
  let inflightCartRequest = null;
  let mutationQueue = Promise.resolve();

  class CartError extends Error {
    constructor(message, meta) {
      super(message);
      this.name = "CartError";
      this.httpStatus = meta ? meta.httpStatus : undefined;
      this.response = meta ? meta.response : undefined;
    }
  }

  async function parseResponse(res) {
    const data = await res.json().catch(() => null);
    // ошибки cart API приходят как {status, message, description}
    if (!res.ok || !data || data.status) {
      const message = (data && (data.description || data.message)) || "Cart request failed";
      throw new CartError(message, { httpStatus: res.status, response: data });
    }
    return data;
  }

  function postJson(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(parseResponse);
  }

  // Целиковая корзина (ответ /cart.js, change, update, clear) — в отличие от
  // ответа add.js, где items — только что добавленные строки.
  function isFullCart(data) {
    return (
      !!data &&
      Array.isArray(data.items) &&
      typeof data.item_count === "number" &&
      typeof data.token === "string"
    );
  }

  function cachedCart() {
    const cacheValid = cartCache && Date.now() - cartCacheAt < CART_CACHE_TTL;
    return cacheValid ? cartCache : null;
  }

  // Конфликты с чужими писателями корзины — в Sentry (warning), чтобы видеть
  // их частоту на проде; без Sentry — в консоль.
  function report(message, extra) {
    console.warn("[cart-service] " + message, extra || "");
    const sentry = window.Sentry;
    if (!sentry || typeof sentry.captureMessage !== "function") return;
    try {
      sentry.captureMessage("[cart-service] " + message, { level: "warning", extra: extra || {} });
    } catch (e) {
      /* Sentry не должен ломать корзину */
    }
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function getCart(forceRefresh = false) {
    if (!forceRefresh && cachedCart()) return Promise.resolve(cartCache);
    if (inflightCartRequest) return inflightCartRequest;

    inflightCartRequest = fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(parseResponse)
      .then((cart) => {
        cartCache = cart;
        cartCacheAt = Date.now();
        inflightCartRequest = null;
        return cart;
      })
      .catch((error) => {
        inflightCartRequest = null;
        throw error;
      });

    return inflightCartRequest;
  }

  function invalidateCache() {
    cartCache = null;
    cartCacheAt = 0;
  }

  // Ответ мутации — это уже целиковая корзина. Без этого каждый потребитель
  // после изменения шёл за теми же данными в /cart.js заново. Ответ add.js
  // корзиной не является — им кэш не греем, а сбрасываем.
  function primeCache(cart) {
    if (!isFullCart(cart)) {
      invalidateCache();
      return cart || null;
    }
    cartCache = cart;
    cartCacheAt = Date.now();
    return cart;
  }

  function linesForVariant(cart, variantId) {
    const id = Number(variantId);
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    return items.filter((item) => item.id === id);
  }

  function totalQuantity(cart, variantId) {
    return linesForVariant(cart, variantId).reduce((sum, item) => sum + item.quantity, 0);
  }

  function hasKeys(obj) {
    return !!obj && typeof obj === "object" && Object.keys(obj).length > 0;
  }

  // Строка для add.js из {variantId|id, quantity, properties, sellingPlan|selling_plan}
  function normalizeItem(item) {
    const line = { id: Number(item.variantId || item.id), quantity: item.quantity || 1 };
    const plan = item.sellingPlan || item.selling_plan;
    if (plan) line.selling_plan = plan;
    if (hasKeys(item.properties)) line.properties = item.properties;
    return line;
  }

  function withSections(body, sections, sectionsUrl) {
    // пустой список (страницы без глобальной шапки) не шлём вовсе
    if (Array.isArray(sections) ? sections.length : sections) body.sections = sections;
    if (sectionsUrl) body.sections_url = sectionsUrl;
    return body;
  }

  // Все мутации корзины идут через одну очередь: параллельные вызовы
  // (двойной клик, несколько компонентов на странице) не гоняются друг с другом.
  function enqueueMutation(taskFn, kind) {
    const detail = { kind: kind || "lines" };
    const run = mutationQueue.then(() => {
      document.dispatchEvent(new CustomEvent("cart:mutation-start", { detail }));
      return Promise.resolve()
        .then(() => taskFn())
        .finally(() => document.dispatchEvent(new CustomEvent("cart:mutation-end", { detail })));
    });
    mutationQueue = run.catch(() => {});
    return run;
  }

  function emitMutation(action, detail) {
    document.dispatchEvent(new CustomEvent("cart:mutated", { detail: Object.assign({ action }, detail) }));
    // совместимость с существующими слушателями темы (cart-additions.js и др.)
    document.dispatchEvent(new CustomEvent("cart-updated", { detail: detail.data || null }));
  }

  // Ядро «несколько строк + атрибуты одним /cart/update.js».
  // НЕ ставится в очередь само — вызывается только изнутри enqueued-операций.
  // Возвращает корзину или null, если слать было нечего.
  async function updateCore(options, retrying) {
    const { updates, attributes, sections, sectionsUrl } = options || {};
    const body = {};
    if (hasKeys(updates)) body.updates = updates;
    if (hasKeys(attributes)) body.attributes = attributes;
    if (!body.updates && !body.attributes) return null;

    let data;
    try {
      data = await postJson("/cart/update.js", withSections(body, sections, sectionsUrl));
    } catch (error) {
      // Ключ строки исчез между снимком и записью (апп Service Fee снял свой
      // сбор, другая вкладка) — Shopify отвечает 422 на ВЕСЬ запрос, ничего не
      // применив. Один повтор по свежей корзине только с существующими ключами
      // (ключи-variant id не проверяем — ими пишет апп, не мы); 422 по другой
      // причине повторять нечего — уходит выше.
      const stale = !retrying && body.updates && error instanceof CartError && error.httpStatus === 422;
      if (!stale) throw error;
      const fresh = await getCart(true);
      const present = fresh.items.map((item) => item.key);
      const kept = {};
      const dropped = [];
      Object.keys(updates).forEach((key) => {
        if (key.indexOf(":") === -1 || present.indexOf(key) !== -1) kept[key] = updates[key];
        else dropped.push(key);
      });
      if (!dropped.length) throw error;
      report("update.js retried without vanished line keys", { dropped: dropped, status: error.httpStatus });
      if (!hasKeys(kept) && !hasKeys(attributes)) return fresh;
      return updateCore({ updates: kept, attributes, sections, sectionsUrl }, true);
    }
    primeCache(data);
    return data;
  }

  // Ядро сброса сессии: все строки в 0 + атрибуты одним запросом вместо
  // clear.js + update.js. Список ключей — из известной корзины (options.cart,
  // например ответ только что прошедшего change) или из свежего /cart.js:
  // кэшу тут не верим, строки, добавленные мимо сервиса за TTL, пережили бы
  // сброс. НЕ ставится в очередь само.
  async function resetCore(options) {
    const { attributes, sections, sectionsUrl } = options || {};
    const cart = (options && isFullCart(options.cart) ? options.cart : null) || (await getCart(true));
    const updates = {};
    cart.items.forEach((item) => {
      updates[item.key] = 0;
    });
    const data = await updateCore({ updates, attributes, sections, sectionsUrl });
    return data || cart;
  }

  // Ядро приведения суммарного количества варианта к потолку: все строки
  // варианта правятся одним /cart/update.js. Корзину принимает от вызывающего
  // (он её только что прочитал), иначе читает сама.
  // НЕ ставится в очередь само — вызывается только изнутри enqueued-операций.
  async function clampCore(variantId, maxTotal, sections, known) {
    const cart = isFullCart(known) ? known : await getCart(true);
    const lines = linesForVariant(cart, variantId);
    let budget = Math.max(0, maxTotal);
    const updates = {};

    lines.forEach((line) => {
      const target = Math.min(line.quantity, budget);
      budget -= target;
      if (line.quantity !== target) updates[line.key] = target;
    });

    const data = await updateCore({ updates, sections });

    if (data) emitMutation("clamp", { variantId: Number(variantId), data });

    return {
      status: data ? "clamped" : "unchanged",
      quantityInCart: Math.min(totalQuantity(cart, variantId), Math.max(0, maxTotal)),
      data,
    };
  }

  // Идемпотентное добавление с потолком: суммарное количество варианта
  // в корзине никогда не превысит maxInCart, сколько бы раз нас ни вызвали
  // (другая вкладка, bfcache, повторный клик).
  function addItem(options) {
    const {
      variantId,
      quantity = 1,
      properties,
      sellingPlan,
      maxInCart = Infinity,
      sections,
      sectionsUrl,
    } = options || {};

    if (!variantId) return Promise.reject(new CartError("variantId is required"));

    return enqueueMutation(async () => {
      const cart = await getCart(true);
      const have = totalQuantity(cart, variantId);

      // корзина уже выше лимита (сломана другим источником) — самолечение
      if (have > maxInCart) return clampCore(variantId, maxInCart, sections, cart);

      if (have >= maxInCart) {
        return { status: "at-limit", added: 0, quantityInCart: have, data: null };
      }

      const toAdd = Math.min(quantity, maxInCart - have);
      const body = { id: Number(variantId), quantity: toAdd };
      if (properties) body.properties = properties;
      if (sellingPlan) body.selling_plan = sellingPlan;
      withSections(body, sections, sectionsUrl);

      const data = await postJson("/cart/add.js", body);
      invalidateCache();
      emitMutation("add", { variantId: Number(variantId), data });

      return {
        status: toAdd < quantity ? "added-partial" : "added",
        added: toAdd,
        quantityInCart: have + toAdd,
        lineKey: data.key || null,
        data,
      };
    });
  }

  // Несколько строк одним /cart/add.js (+ attributes, которые add.js принимает
  // вместе со строками — так уже шлёт форма товара). Лимитов нет: кто зовёт,
  // тот и решает по снимку корзины (getCart), что класть.
  function addItems(options) {
    const { items, attributes, sections, sectionsUrl } = options || {};

    if (!Array.isArray(items) || items.length === 0) {
      return Promise.reject(new CartError("items are required"));
    }

    return enqueueMutation(async () => {
      const body = { items: items.map(normalizeItem) };
      if (hasKeys(attributes)) body.attributes = attributes;
      withSections(body, sections, sectionsUrl);

      const data = await postJson("/cart/add.js", body);
      invalidateCache();
      emitMutation("add", { data });
      return { status: "added", data };
    });
  }

  // Абсолютное суммарное количество варианта (вниз — через update, вверх — через add)
  function setVariantQuantity(options) {
    const { variantId, quantity, properties, sections } = options || {};

    if (!variantId) return Promise.reject(new CartError("variantId is required"));

    return enqueueMutation(async () => {
      const cart = await getCart(true);
      const have = totalQuantity(cart, variantId);
      const target = Math.max(0, quantity);

      if (target === have) return { status: "unchanged", quantityInCart: have, data: null };
      if (target < have) return clampCore(variantId, target, sections, cart);

      const body = { id: Number(variantId), quantity: target - have };
      if (properties) body.properties = properties;
      withSections(body, sections);

      const data = await postJson("/cart/add.js", body);
      invalidateCache();
      emitMutation("add", { variantId: Number(variantId), data });
      return { status: "set", quantityInCart: target, data };
    });
  }

  function removeVariant(options) {
    const { variantId, sections } = options || {};
    return setVariantQuantity({ variantId, quantity: 0, sections });
  }

  // Публичное «ужать до лимита» — для self-heal при рендере
  function enforceLimit(options) {
    const { variantId, maxInCart, sections } = options || {};
    return enqueueMutation(() => clampCore(variantId, maxInCart, sections));
  }

  function getVariantQuantity(variantId, forceRefresh = false) {
    return getCart(forceRefresh).then((cart) => totalQuantity(cart, variantId));
  }

  // Одна строка по ключу: количество (> 0), свойства, selling plan — через
  // /cart/change.js, ответ — целиковая корзина. Удалять строки так НЕЛЬЗЯ:
  // апп Service Fee перехватывает fetch-ответы /cart/change и, оставшись в
  // корзине один, сам снимает сбор и перезагружает страницу. Удаление — только
  // updateLines по плану MFClub.removalPlan.
  function changeLine(options) {
    const { key, quantity, properties, sellingPlan, sections, sectionsUrl } = options || {};

    if (!key) return Promise.reject(new CartError("key is required"));
    if (quantity !== undefined && !(quantity > 0)) {
      return Promise.reject(new CartError("changeLine is for quantity > 0; remove lines with updateLines"));
    }

    return enqueueMutation(async () => {
      const body = { id: key };
      if (quantity !== undefined) body.quantity = quantity;
      if (properties) body.properties = properties;
      if (sellingPlan !== undefined) body.selling_plan = sellingPlan;
      withSections(body, sections, sectionsUrl);

      const data = await postJson("/cart/change.js", body);
      primeCache(data);
      emitMutation("change", { key, data });
      return { status: "changed", data };
    });
  }

  // Несколько строк и/или атрибуты одним /cart/update.js. Это основной
  // способ удалять: план «какие строки снять вместе с этой» даёт
  // MFClub.removalPlan, здесь только транспорт.
  function updateLines(options) {
    const { updates, attributes } = options || {};

    if (!hasKeys(updates) && !hasKeys(attributes)) {
      return Promise.reject(new CartError("updates or attributes are required"));
    }

    return enqueueMutation(async () => {
      const data = await updateCore(options);
      if (!data) return { status: "unchanged", data: null };
      emitMutation("update-lines", { data });
      return { status: "updated", data };
    }, hasKeys(updates) ? "lines" : "attributes");
  }

  // Сброс сессии: все строки в 0 + атрибуты (delivery_date, club_offer,
  // order_source…) одним запросом вместо пары clear.js + update.js.
  // Передавай cart (ответ предыдущей мутации), если он есть, — тогда без GET.
  function resetSession(options) {
    return enqueueMutation(async () => {
      const data = await resetCore(options);
      emitMutation("reset", { data });
      return { status: "reset", data };
    });
  }

  function replaceLines(options) {
    const { match, add, sections } = options || {};

    if (typeof match !== "function") return Promise.reject(new CartError("match is required"));

    return enqueueMutation(async () => {
      const cart = await getCart(true);
      const keys = cart.items.filter(match).map((item) => item.key);
      let data = null;

      try {
        if (keys.length) {
          const updates = {};
          keys.forEach((key) => {
            updates[key] = 0;
          });
          const body = { updates };
          if (sections && !add) body.sections = sections;

          data = await postJson("/cart/update.js", body);
          if (data.items.some(match)) throw new CartError("Lines were not removed", { response: data });
          primeCache(data);
        }

        if (add) {
          const body = normalizeItem(add);
          if (sections) body.sections = sections;

          data = await postJson("/cart/add.js", body);
          invalidateCache();
        }
      } catch (error) {
        invalidateCache();
        throw error;
      }

      if (keys.length || add) emitMutation("replace-lines", { removed: keys.length, data });

      return {
        status: keys.length || add ? "replaced" : "unchanged",
        removed: keys.length,
        added: add ? 1 : 0,
        data: data || cart,
      };
    });
  }

  // ─── Сборка корзины под чекаут ──────────────────────────────────────────────

  // Свойства строки для сравнения «наша ли это строка»: пустые значения Shopify
  // не хранит, остальное — строками.
  function normalizeProps(props) {
    const out = {};
    Object.keys(props || {}).forEach((key) => {
      const value = props[key];
      if (value === null || value === undefined || value === "") return;
      out[key] = String(value);
    });
    return out;
  }

  function sameProps(a, b) {
    const left = normalizeProps(a);
    const right = normalizeProps(b);
    const keys = Object.keys(left);
    if (keys.length !== Object.keys(right).length) return false;
    return keys.every((key) => left[key] === right[key]);
  }

  function linePlanId(item) {
    const allocation = item && item.selling_plan_allocation;
    return allocation && allocation.selling_plan ? Number(allocation.selling_plan.id) : 0;
  }

  // Строка из add.js (normalizeItem) против строки /cart.js
  function sameVariantAndPlan(expected, item) {
    return Number(item.variant_id) === expected.id && linePlanId(item) === Number(expected.selling_plan || 0);
  }

  function lineMatches(expected, item) {
    return sameVariantAndPlan(expected, item) && sameProps(expected.properties, item.properties);
  }

  // 'present' — строка есть (Shopify сливает одинаковые строки, поэтому
  // количество сравниваем как «не меньше»); 'variant' — тот же вариант и план,
  // но другие свойства: не наша, но и дублировать букет нельзя; 'missing' — нет.
  function presence(cart, expected) {
    const same = cart.items.filter((item) => sameVariantAndPlan(expected, item));
    const ours = same.filter((item) => sameProps(expected.properties, item.properties));
    const quantity = ours.reduce((sum, item) => sum + item.quantity, 0);
    if (quantity >= expected.quantity) return "present";
    if (same.length) return "variant";
    return "missing";
  }

  // Ключи атрибутов, у которых в корзине не то значение, что мы писали
  // (пустая строка = ключа быть не должно).
  function attributeDiff(cart, attributes) {
    const diff = {};
    const current = (cart && cart.attributes) || {};
    Object.keys(attributes || {}).forEach((key) => {
      const want = attributes[key] === null || attributes[key] === undefined ? "" : String(attributes[key]);
      const have = current[key] === null || current[key] === undefined ? "" : String(current[key]);
      if (want !== have) diff[key] = attributes[key];
    });
    return hasKeys(diff) ? diff : null;
  }

  // После add.js корзина перечитывается и приводится к ожидаемой: строки на
  // месте (иначе add повторяется), атрибуты те (иначе один update.js), а при
  // exclusive — нет чужих строк. Чужую строку снимаем НЕ БОЛЬШЕ ОДНОГО РАЗА:
  // если она вернулась, её держит апп (сбор по его правилам), и воевать с ним
  // нельзя — идём в чекаут с ней, но пишем в Sentry. Падаем только когда наши
  // строки не удалось поставить за SETTLE_MAX_REPAIRS ремонтов или
  // SETTLE_BUDGET_MS. Первое чистое чтение — финал: апп к этому моменту уже
  // перечитал корзину после нашего add (он ждёт свой GET внутри обёртки fetch)
  // и промолчал; контрольное чтение с паузой — только после ремонта.
  async function settleCheckout(options) {
    const { expected, attributes, exclusive } = options;
    const started = Date.now();
    const removedOnce = {};
    let repairs = 0;
    let recheck = false;

    for (;;) {
      if (recheck) await sleep(SETTLE_WINDOW_MS);
      const cart = await getCart(true);

      const missing = expected.filter((line) => presence(cart, line) === "missing");
      const variantOnly = expected.filter((line) => presence(cart, line) === "variant");
      const foreignAll = exclusive
        ? cart.items.filter((item) => !expected.some((line) => lineMatches(line, item)))
        : [];
      const returned = foreignAll.filter((item) => removedOnce[item.key]);
      const foreign = foreignAll.filter((item) => !removedOnce[item.key]);
      const attrFix = attributeDiff(cart, attributes);

      if (variantOnly.length) {
        report("checkout line present with other properties; not duplicating it", {
          variants: variantOnly.map((line) => line.id),
        });
      }
      if (returned.length) {
        report("foreign cart line came back after removal; leaving it to its owner", {
          keys: returned.map((item) => item.key),
        });
      }

      if (!missing.length && !foreign.length && !attrFix) return cart;

      const overBudget = repairs >= SETTLE_MAX_REPAIRS || Date.now() - started > SETTLE_BUDGET_MS;
      if (overBudget) {
        if (missing.length) {
          throw new CartError("Cart did not settle before checkout", { response: cart });
        }
        report("checkout cart left with foreign lines or attributes after repairs", {
          foreign: foreign.map((item) => item.key),
          attributes: attrFix ? Object.keys(attrFix) : [],
        });
        return cart;
      }

      repairs += 1;
      report("checkout cart repaired", {
        repair: repairs,
        missing: missing.map((line) => line.id),
        foreign: foreign.map((item) => item.key),
        attributes: attrFix ? Object.keys(attrFix) : [],
      });

      if (foreign.length || attrFix) {
        const updates = {};
        foreign.forEach((item) => {
          updates[item.key] = 0;
          removedOnce[item.key] = true;
        });
        await updateCore({ updates, attributes: attrFix || undefined });
      }
      if (missing.length) {
        await postJson("/cart/add.js", { items: missing });
        invalidateCache();
      }
      recheck = true;
    }
  }

  // «Собрать корзину под чекаут» одной операцией очереди:
  //   clear: true | 'auto' — /cart/clear.js (без снимка корзины: нет ни
  //     устаревших ключей, ни 422; снимает и строку сбора аппа, так что ему
  //     нечего дописывать; 'auto' — алиас из версии с кэшем);
  //   clear: function (cart) → { updates, attributes } | null — план от слоя
  //     правил (MFClub.clubFlowPlan) по свежему /cart.js: один update.js;
  //   clear: false — ничего не снимаем.
  // Затем ОДИН /cart/add.js со всеми строками и attributes (add.js принимает
  // атрибуты, пустая строка удаляет ключ — проверено на сторе), затем
  // settleCheckout. exclusive: true — в корзине не должно быть ничего, кроме
  // items (клубные заказы); в остальных потоках чужие строки (сбор аппа)
  // легитимны и не трогаются.
  // Результат и событие несут устоявшуюся целиковую корзину.
  // ВНИМАНИЕ: assets/mf-attribution.js оборачивает эту функцию и дописывает в
  // attributes свои _mf_* (данные для серверной отправки покупок в Meta) —
  // сигнатуру prepareCheckout({ items, attributes, clear }) не менять, новые
  // ключи — только добавлять.
  function prepareCheckout(options) {
    const { items, attributes, clear = "auto", exclusive = false } = options || {};

    if (!Array.isArray(items) || items.length === 0) {
      return Promise.reject(new CartError("items are required"));
    }

    const expected = items.map(normalizeItem);
    const planFn = typeof clear === "function" ? clear : null;
    const wipe = clear === true || clear === "auto";

    return enqueueMutation(async () => {
      let expectedAttributes = hasKeys(attributes) ? Object.assign({}, attributes) : {};

      try {
        if (wipe) {
          await postJson("/cart/clear.js", {});
        } else if (planFn) {
          const plan = planFn(await getCart(true));
          if (plan && (hasKeys(plan.updates) || hasKeys(plan.attributes))) {
            await updateCore({ updates: plan.updates, attributes: plan.attributes });
            expectedAttributes = Object.assign({}, plan.attributes, expectedAttributes);
          }
        }

        const body = { items: expected };
        if (hasKeys(attributes)) body.attributes = attributes;
        await postJson("/cart/add.js", body);
      } finally {
        // ответ add.js корзиной не является, а clear/update уже устарели
        invalidateCache();
      }

      const cart = await settleCheckout({
        expected,
        attributes: expectedAttributes,
        exclusive: !!exclusive,
      });
      emitMutation("prepare-checkout", { data: cart });
      return { status: "ready", data: cart };
    });
  }

  // Атрибуты корзины (delivery_date и прочий контракт с OMS) — тоже в очередь,
  // чтобы запись атрибутов не разъезжалась с add/change.
  // Мержится Shopify-ом: передавай только те ключи, которые меняешь.
  function updateAttributes(attributes, options) {
    const { sections, sectionsUrl } = options || {};

    if (!attributes || typeof attributes !== "object") {
      return Promise.reject(new CartError("attributes object is required"));
    }

    return enqueueMutation(async () => {
      const data = await postJson("/cart/update.js", withSections({ attributes }, sections, sectionsUrl));
      primeCache(data);
      emitMutation("update-attributes", { data });
      return { status: "updated", data };
    }, "attributes");
  }

  window.CartService = {
    getCart,
    getVariantQuantity,
    addItem,
    addItems,
    setVariantQuantity,
    removeVariant,
    enforceLimit,
    changeLine,
    updateLines,
    resetSession,
    replaceLines,
    updateAttributes,
    prepareCheckout,
    invalidateCache,
    primeCache,
    isFullCart,
    CartError,
  };
})();
